import * as Api from '@ViewModels';

/**
 * Gets reminders to keep in touch with contacts
 * @source Original location: UiUtils.ts.backup line 641
 */
export const remindersToKeepInTouchWithContacts = (reminders: Api.ActionItemViewModel[]) => {
	const results: {
		kitActionItems: Api.ActionItemViewModel[];
		contacts: Api.ContactViewModel[];
	} = {
		contacts: [],
		kitActionItems: [],
	};

	results.kitActionItems = (reminders || []).filter(x => {
		// include kit action items that aren't suggested, and/or suggested kits that have active tag alerts
		return (
			(!!x.isKeepInTouchActionItem && !x.isSuggestedKeepInTouchActionItem) ||
			(!!x.isSuggestedKeepInTouchActionItem &&
				!!x.keepInTouchReference.contact.tagAlerts &&
				x.keepInTouchReference.contact.tagAlerts.length > 0)
		);
	});

	results.contacts = (results.kitActionItems || []).reduce<Api.ContactViewModel[]>((result, actionItem) => {
		result.push(actionItem.keepInTouchReference.contact);
		return result;
	}, []);

	return results;
};

/**
 * Gets tag alert duration string value
 * @source Original location: UiUtils.ts.backup line 844
 */
export const tagAlertDurationStringValue = (tagAlert: Api.TagAlertViewModel) => {
	if (!tagAlert) {
		return '';
	}

	let calendarUnit = 0;
	let calendarUnitLabel = '';

	if (tagAlert.interactionInterval % 30 === 0) {
		calendarUnit = tagAlert.interactionInterval / 30;
		calendarUnitLabel = 'month';
	} else if (tagAlert.interactionInterval === 365) {
		calendarUnit = 12;
		calendarUnitLabel = 'month';
	} else if (tagAlert.interactionInterval % 7 === 0) {
		calendarUnit = tagAlert.interactionInterval / 7;
		calendarUnitLabel = 'week';
	} else {
		calendarUnit = tagAlert.interactionInterval;
		calendarUnitLabel = 'day';
	}

	if (calendarUnit > 1) {
		calendarUnitLabel += 's';
	}

	return `${calendarUnit} ${calendarUnitLabel}`;
};

/**
 * Checks if criteria is tag search contact filter criteria
 * @source Original location: UiUtils.ts.backup line 1058
 */
export const isTagSearchContactFilterCriteria = (criteria: Api.IContactFilterCriteria): boolean => {
	if (criteria) {
		if (!!criteria.criteria && criteria.criteria.length > 0) {
			return !!criteria.criteria.every(x => !!isTagSearchContactFilterCriteria(x));
		} else if (criteria.property === Api.ContactFilterCriteriaProperty.Tag) {
			return true;
		}
	}

	return false;
};

/**
 * Flattens the IContactFilterCriteria to a list containing just policy filters
 * @source Original location: UiUtils.ts.backup line 1101
 */
export const getPolicyFilterCriteriaInCriteria = (
	criteria?: Api.IContactFilterCriteria
): Api.IContactFilterCriteria[] => {
	if (criteria?.criteria?.length) {
		return criteria.criteria.reduce((res, curr) => {
			return res.concat(getPolicyFilterCriteriaInCriteria(curr));
		}, []);
	}

	if (criteria?.property === Api.ContactFilterCriteriaProperty.Policy && !!criteria.value) {
		return [criteria];
	}

	return [];
};

/**
 * Sorts contact tags
 * @source Original location: UiUtils.ts.backup line 1518
 */
export const sortContactTags = ({
	tags,
	priorityTags = [],
}: {
	tags: Api.IAccountTag[];
	priorityTags?: string[];
}): Api.IAccountTag[] => {
	return tags.sort((a, b) => {
		if (priorityTags.includes(a.name)) {
			return -1;
		}
		if (priorityTags.includes(b.name)) {
			return 1;
		}
		return 0;
	});
};

/**
 * Gets visible tags
 * @source Original location: UiUtils.ts.backup line 1537
 */
export const getVisibleTags = (
	entity: Api.EntityViewModel,
	highPriorityTags?: Api.ObservableCollection<Api.TagViewModel>
): [Api.IAccountTag | null, Api.IAccountTag[]] => {
	if (entity?.tagsCollection?.length > 0) {
		const highlighted =
			(!!highPriorityTags && highPriorityTags.length > 0 ? highPriorityTags.map(x => x.value) : null) || [];

		// bring the highlighted tags to the front of the list, then tags with alerts... etc
		// only show the first 3
		const sortedTags = [...(entity.tagsCollection || [])].sort((a, b) => {
			if (highlighted.indexOf(a.name) >= 0) {
				return -1;
			} else if (highlighted.indexOf(b.name) >= 0) {
				return 1;
			} else if (entity instanceof Api.ContactViewModel && !!entity.getTagAlertForTagValue(a.name)) {
				return -1;
			} else if (entity instanceof Api.ContactViewModel && !!entity.getTagAlertForTagValue(b.name)) {
				return 1;
			}
			return 0;
		});
		const visibleTag = sortedTags.shift();
		return [visibleTag, sortedTags];
	}

	return [null, []];
};
